// Relay core: identity, addresses, check words, handshake and chunk encryption.
// Pure functions shared by the web app, the Worker and the tests.
// Uses audited libraries only (noble / scure). No home-made crypto primitives.

import { ed25519, x25519 } from '@noble/curves/ed25519.js';
const edwardsToMontgomeryPub = (p) => ed25519.utils.toMontgomery(p), edwardsToMontgomeryPriv = (k) => ed25519.utils.toMontgomerySecret(k);
import { sha256 } from '@noble/hashes/sha2.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { randomBytes, concatBytes, utf8ToBytes, bytesToHex, hexToBytes } from '@noble/hashes/utils.js';
import { scrypt, scryptAsync } from '@noble/hashes/scrypt.js';
import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english.js';
import { bech32m, base64, base64urlnopad, utils as baseUtils } from '@scure/base';

export const VERSION = 'drop/v1'; // identity derivation (must never change, or addresses change)
export const PROTOCOL = 2;          // wire protocol
export const CHUNK = 64 * 1024;
const te = (s) => utf8ToBytes(s);

// ---------------------------------------------------------------- identity
// Recovery words. New identities get 6 words (64 bits plus a 2-bit checksum, from the
// same 2048-word BIP39 list). Because 6 words are far easier to guess than 12, they are
// stretched with scrypt (64 MB, about 1 to 2 seconds on a phone) before a key is made,
// so every guess costs an attacker the same. Older 12-word phrases keep their original
// derivation, so existing addresses never change.
export const SHORT_WORDS = 6;
export const STRETCH = { N: 2 ** 16, r: 8, p: 1, dkLen: 32 };
const SHORT_SALT = te('relay/v2/identity-6-words');
const shortChecksum = (e) => new Uint8Array([(sha256(e)[0] >> 6) << 6]); // first 2 bits of SHA-256, as BIP39
const shortCoder = baseUtils.chain(baseUtils.checksum(1, shortChecksum), baseUtils.radix2(11, true), baseUtils.alphabet(wordlist));
const INVALID = 'Those words aren’t right. Check each word and the order.';

export function newMnemonic() {
  return shortCoder.encode(randomBytes(8)).join(' '); // 6 words
}

export function newLongMnemonic() {
  return generateMnemonic(wordlist, 128); // 12 words, the original format
}

export function normaliseMnemonic(m) {
  return String(m || '').trim().toLowerCase().split(/\s+/).join(' ');
}

// 'short' (6 words), 'long' (12 to 24 BIP39 words) or null if the words are not valid.
export function phraseKind(m) {
  const phrase = normaliseMnemonic(m), words = phrase.split(' ');
  if (words.length === SHORT_WORDS) { try { const e = shortCoder.decode(words); return e.length === 8 ? 'short' : null; } catch { return null; } }
  return validateMnemonic(phrase, wordlist) ? 'long' : null;
}

export function isValidMnemonic(m) {
  return !!phraseKind(m);
}

const idFromSecret = (secret) => { const pub = ed25519.getPublicKey(secret); return { secret, pub, address: addressFromPub(pub) }; };

// The slow step for 6-word phrases. Returns 32 bytes that can be kept (encrypted) so a
// device doesn't have to repeat it every time it unlocks.
export async function stretchWords(m, onProgress) {
  const words = normaliseMnemonic(m).split(' ');
  if (phraseKind(m) !== 'short') throw new Error(INVALID);
  return scryptAsync(shortCoder.decode(words), SHORT_SALT, { ...STRETCH, onProgress });
}

export function identityFromStretched(k) {
  const key = typeof k === 'string' ? hexToBytes(k) : k;
  if (!(key instanceof Uint8Array) || key.length !== 32) throw new Error(INVALID);
  return idFromSecret(hkdf(sha256, key, te('relay/v2'), te('identity-ed25519'), 32));
}

function identityFromLong(phrase) {
  const seed = mnemonicToSeedSync(phrase);
  return idFromSecret(hkdf(sha256, seed, te(VERSION), te('identity-ed25519'), 32));
}

// Synchronous: fine for 12-word phrases, and for 6-word ones in tests and tools. The app
// uses identityFromPhrase so the slow step doesn't freeze the screen.
export function identityFromMnemonic(m) {
  const phrase = normaliseMnemonic(m), kind = phraseKind(phrase);
  if (!kind) throw new Error(INVALID);
  if (kind === 'long') return identityFromLong(phrase);
  return { ...identityFromStretched(scrypt(shortCoder.decode(phrase.split(' ')), SHORT_SALT, STRETCH)) };
}

// Returns the identity and, for 6-word phrases, the stretched key to cache.
export async function identityFromPhrase(m, onProgress) {
  const phrase = normaliseMnemonic(m), kind = phraseKind(phrase);
  if (!kind) throw new Error(INVALID);
  if (kind === 'long') return { id: identityFromLong(phrase), stretched: null };
  const k = await stretchWords(phrase, onProgress);
  return { id: identityFromStretched(k), stretched: bytesToHex(k) };
}

// ---------------------------------------------------------------- send by link
// A one-time key for someone without Relay. It travels after the # of the link, which
// browsers never send to a server, so only whoever holds the link can receive.
export const LINK_KEY_BYTES = 16;
export const LINK_LIFE = 48 * 60 * 60 * 1000;
export function newLinkKey() { return base64urlnopad.encode(randomBytes(LINK_KEY_BYTES)); }
export function identityFromLinkKey(key) {
  let k; try { k = base64urlnopad.decode(String(key || '')); } catch { k = null; }
  if (!k || k.length !== LINK_KEY_BYTES) throw new Error('This link is incomplete. Ask the sender to copy it again.');
  return idFromSecret(hkdf(sha256, k, te('relay/link'), te('identity-ed25519'), 32));
}

// ---------------------------------------------------------------- addresses
// An address is the full public key, bech32m-encoded with a checksum, so a typo is caught.
export const HRP = 'relay';
const LEGACY_HRP = ['drop']; // addresses from before the rename still work
export function addressFromPub(pub, hrp = HRP) {
  return bech32m.encode(hrp, bech32m.toWords(pub), 90);
}
// Any accepted form (relay1… or the older drop1…) back to today's relay1… form.
export function normaliseAddress(addr) { return addressFromPub(pubFromAddress(addr)); }
export function legacyAddress(pub) { return addressFromPub(pub, 'drop'); }

export function pubFromAddress(addr) {
  const a = String(addr || '').trim().toLowerCase().replace(/\s+/g, '');
  let dec;
  try { dec = bech32m.decode(a, 90); } catch { throw new Error('That address is not valid. Check it was copied in full.'); }
  if (dec.prefix !== HRP && !LEGACY_HRP.includes(dec.prefix)) throw new Error('That is not a Relay address.');
  const pub = Uint8Array.from(bech32m.fromWords(dec.words));
  if (pub.length !== 32) throw new Error('That address is the wrong length.');
  return pub;
}

export function isValidAddress(addr) {
  try { pubFromAddress(addr); return true; } catch { return false; }
}

export function shortAddress(addr) {
  const p = addr.indexOf('1') + 1;
  return addr.length > 16 ? `${addr.slice(0, p + 4)}…${addr.slice(-5)}` : addr;
}

export function groupedAddress(addr) {
  const p = addr.indexOf('1') + 1; // keep "relay1" together, then groups of four
  return [addr.slice(0, p), ...(addr.slice(p).match(/.{1,4}/g) || [])].join(' ');
}

// The server only ever sees a hash of the public key as the inbox name.
export function inboxId(pub) {
  return bytesToHex(sha256(concatBytes(te('drop/inbox'), pub))).slice(0, 40);
}

// ---------------------------------------------------------------- check words
// Same three words on both devices for a given pair of keys, like Signal's safety number.
export function checkWords(pubA, pubB) {
  const [x, y] = [pubA, pubB].sort(compareBytes);
  const h = sha256(concatBytes(te('drop/check'), x, y));
  const n = (h[0] << 16) | (h[1] << 8) | h[2];
  const n2 = (h[3] << 16) | (h[4] << 8) | h[5];
  return [wordlist[(n >>> 13) & 2047], wordlist[(n >>> 2) & 2047], wordlist[(n2 >>> 5) & 2047]];
}

export function compareBytes(a, b) {
  for (let i = 0; i < Math.min(a.length, b.length); i++) if (a[i] !== b[i]) return a[i] - b[i];
  return a.length - b.length;
}

// ---------------------------------------------------------------- identicon
const HUES = ['#4231E8', '#1E7A46', '#B86A00', '#C2185B', '#0F7C8C'];
export function identicon(pub) {
  const h = sha256(concatBytes(te('drop/identicon'), pub));
  const fg = HUES[h[0] % HUES.length];
  const cells = [];
  for (let r = 0; r < 5; r++) for (let c = 0; c < 5; c++) {
    const cc = c < 3 ? c : 4 - c;
    const bit = (h[1 + r] >> cc) & 1;
    cells.push(bit ? fg : '#D5DCD7');
  }
  return cells;
}

// ---------------------------------------------------------------- signing
// Every field is length-prefixed, so two different lists of fields can never
// produce the same signed bytes. The label separates each kind of signature.
export function transcript(label, ...parts) {
  const items = [te(`drop/sig/v${PROTOCOL}`), te(label), ...parts.map(toBytes)];
  const out = [];
  for (const b of items) { const n = new Uint8Array(4); new DataView(n.buffer).setUint32(0, b.length); out.push(n, b); }
  return sha256(concatBytes(...out));
}
export function sign(secret, label, ...parts) {
  return ed25519.sign(transcript(label, ...parts), secret);
}
export function verify(pub, sig, label, ...parts) {
  try {
    if (!(sig instanceof Uint8Array) || sig.length !== 64 || !(pub instanceof Uint8Array) || pub.length !== 32) return false;
    return ed25519.verify(sig, transcript(label, ...parts), pub);
  } catch { return false; }
}
function toBytes(x) {
  if (x instanceof Uint8Array) return x;
  return te(String(x));
}

// ---------------------------------------------------------------- handshake
// Each transfer uses fresh X25519 keys (forward secrecy). Each side signs its
// ephemeral key and its WebRTC description with its identity key, which binds the
// encrypted channel (and the DTLS fingerprint inside the SDP) to the real people.
export function newEphemeral() {
  const secret = x25519.utils.randomSecretKey();
  return { secret, pub: x25519.getPublicKey(secret) };
}

export function signOffer(id, eph, toPub, sdp, ts, nonce) {
  return sign(id.secret, 'drop/offer', id.pub, toPub, eph.pub, sdp, String(ts), nonce);
}
export function verifyOffer(fromPub, toPub, ephPub, sdp, ts, nonce, sig) {
  return verify(fromPub, sig, 'drop/offer', fromPub, toPub, ephPub, sdp, String(ts), nonce);
}
export function signAnswer(id, eph, offerFromPub, offerEphPub, sdp) {
  return sign(id.secret, 'drop/answer', id.pub, offerFromPub, offerEphPub, eph.pub, sdp);
}
export function verifyAnswer(answerPub, offerFromPub, offerEphPub, ephPub, sdp, sig) {
  return verify(answerPub, sig, 'drop/answer', answerPub, offerFromPub, offerEphPub, ephPub, sdp);
}

// Derive one key per direction. "a" is the side that made the offer (the sender).
export function sessionKeys(myEph, peerEphPub, aPub, bPub, aEphPub, bEphPub) {
  const shared = x25519.getSharedSecret(myEph.secret, peerEphPub);
  const salt = sha256(concatBytes(te('drop/session'), aPub, bPub, aEphPub, bEphPub));
  return {
    a2b: hkdf(sha256, shared, salt, te('a2b'), 32),
    b2a: hkdf(sha256, shared, salt, te('b2a'), 32),
    transcript: salt,
  };
}

// ---------------------------------------------------------------- framing
// Every frame on the data channel is AES-256-GCM encrypted. The nonce is a strict
// counter per direction, so a replayed, dropped or reordered frame fails.
export const FRAME = { MANIFEST: 1, ACCEPT: 2, DECLINE: 3, CHUNK: 4, ACK: 5, RECEIPT: 6, ERROR: 7, DONE: 8 };

export class Cipher {
  constructor(rawKey, dirByte) { this.raw = rawKey; this.dir = dirByte; this.counter = 0n; this.key = null; }
  async init() {
    this.key = await crypto.subtle.importKey('raw', this.raw, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    return this;
  }
  nonce() {
    const n = new Uint8Array(12);
    n[0] = this.dir;
    new DataView(n.buffer).setBigUint64(4, this.counter);
    this.counter += 1n;
    return n;
  }
  async seal(type, body) {
    const plain = new Uint8Array(1 + body.length);
    plain[0] = type; plain.set(body, 1);
    const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: this.nonce() }, this.key, plain);
    return new Uint8Array(ct);
  }
  async open(frame) {
    if (!(frame instanceof Uint8Array) || frame.length < 17 || frame.length > CHUNK + 1024) throw new Error('Bad frame');
    const pt = new Uint8Array(await crypto.subtle.decrypt({ name: 'AES-GCM', iv: this.nonce() }, this.key, frame));
    return { type: pt[0], body: pt.subarray(1) };
  }
}

export const enc = {
  json: (o) => te(JSON.stringify(o)),
  fromJson: (b) => JSON.parse(new TextDecoder().decode(b)),
  chunk(fileIdx, chunkIdx, data) {
    const out = new Uint8Array(6 + data.length);
    const v = new DataView(out.buffer);
    v.setUint16(0, fileIdx); v.setUint32(2, chunkIdx);
    out.set(data, 6);
    return out;
  },
  unchunk(b) {
    if (b.length < 6) throw new Error('Bad chunk');
    const v = new DataView(b.buffer, b.byteOffset, b.byteLength);
    return { fileIdx: v.getUint16(0), chunkIdx: v.getUint32(2), data: b.subarray(6) };
  },
};

// ---------------------------------------------------------------- file lists
// The receiver never trusts the sender's numbers or names. Everything is checked
// and rebuilt here before it is shown or used.
export const LIMITS = { files: 1000, nameLen: 120, fileBytes: 8 * 1024 ** 3, totalBytes: 8 * 1024 ** 3 };
const SAFE_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif', 'image/avif',
  'video/mp4', 'video/quicktime', 'video/webm', 'audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'audio/wav', 'application/pdf', 'text/plain']);
export const PREVIEW_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif']);

export function safeType(t) {
  const x = String(t || '').toLowerCase().split(';')[0].trim();
  return SAFE_TYPES.has(x) ? x : 'application/octet-stream';
}

export function safeName(name) {
  let n = String(name ?? '').normalize('NFC')
    .replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2060-\u2069\ufeff]/g, '')
    .replace(/[\\/:*?"<>|]/g, '_').replace(/^[\s.]+/, '').trim();
  if (!n) n = 'file';
  if (n.length > LIMITS.nameLen) {
    const dot = n.lastIndexOf('.'), ext = dot > 0 && n.length - dot <= 10 ? n.slice(dot) : '';
    n = n.slice(0, LIMITS.nameLen - ext.length) + ext;
  }
  return n;
}

export const chunksFor = (size) => Math.max(1, Math.ceil(size / CHUNK));

export function validateManifest(m) {
  if (!m || typeof m !== 'object' || Array.isArray(m)) throw new Error('Bad file list.');
  if (m.v !== PROTOCOL) throw new Error('The sender is using a different version of Relay. Both devices should reload.');
  if (m.chunk !== CHUNK) throw new Error('Bad file list.');
  if (!Array.isArray(m.files) || m.files.length < 1 || m.files.length > LIMITS.files) throw new Error('Too many files in one transfer.');
  let bytes = 0, total = 0;
  const files = m.files.map((f) => {
    if (!f || typeof f !== 'object') throw new Error('Bad file list.');
    const size = f.size;
    if (!Number.isSafeInteger(size) || size < 0 || size > LIMITS.fileBytes) throw new Error('A file in the list has an impossible size.');
    if (typeof f.name !== 'string' || f.name.length > 1000) throw new Error('Bad file name.');
    bytes += size; total += chunksFor(size);
    return { name: safeName(f.name), size, type: safeType(f.type) };
  });
  if (bytes > LIMITS.totalBytes) throw new Error('This transfer is too big.');
  return { v: PROTOCOL, chunk: CHUNK, files, total, bytes };
}

// Key used to encrypt the address book and ledger on this device.
export function dataKey(id) {
  return hkdf(sha256, id.secret, te(VERSION), te('local-data'), 32);
}

export function txId(manifestBytes, ephPub) {
  return '0x' + bytesToHex(sha256(concatBytes(te('drop/tx'), manifestBytes, ephPub)));
}

export function receiptPayload(tx, hashes) {
  return [tx, hashes.join(',')];
}

export const b64 = { enc: (u) => base64.encode(u), dec: (s) => base64.decode(s) };
export { bytesToHex, randomBytes, sha256 };


// ---------------------------------------------------------------- doorbell notes
// When someone is offline, the sender leaves a short note at their inbox: "this address
// wants to send you N files". It is sealed to the recipient's key (only their device can
// open it) and signed by the sender (nobody can fake one). The server only sees ciphertext.
// It also carries a one-time wake code: the recipient hands it back to tell the sender
// "I'm ready", without the server ever learning who is waiting for whom.
export const BELL_LIFE = 48 * 60 * 60 * 1000;
const subtle = () => globalThis.crypto.subtle;
async function gcm(key, iv, data, decrypt) {
  const k = await subtle().importKey('raw', key, 'AES-GCM', false, [decrypt ? 'decrypt' : 'encrypt']);
  return new Uint8Array(await subtle()[decrypt ? 'decrypt' : 'encrypt']({ name: 'AES-GCM', iv }, k, data));
}
export function newWakeCode() {
  const tok = bytesToHex(randomBytes(16));
  return { tok, h: wakeHash(tok) };
}
export function wakeHash(tok) { return bytesToHex(sha256(te('relay/wake/' + tok))); }

export async function sealBell(id, toPub, note) {
  const body = te(JSON.stringify(note));
  if (body.length > 1200) throw new Error('note too big');
  const sig = sign(id.secret, 'relay/bell', toPub, id.pub, body);
  const eph = x25519.utils.randomSecretKey(), ephPub = x25519.getPublicKey(eph);
  const shared = x25519.getSharedSecret(eph, edwardsToMontgomeryPub(toPub));
  const key = hkdf(sha256, shared, concatBytes(ephPub, toPub), te('relay/bell/key'), 32);
  const iv = randomBytes(12);
  const ct = await gcm(key, iv, concatBytes(id.pub, sig, body), false);
  return base64.encode(concatBytes(new Uint8Array([1]), ephPub, iv, ct));
}
// Returns { fromPub, note } or throws if it isn't a genuine note for this device.
export async function openBell(id, box) {
  const b = base64.decode(String(box));
  if (b.length < 1 + 32 + 12 + 16 + 96 || b[0] !== 1) throw new Error('bad note');
  const ephPub = b.slice(1, 33), iv = b.slice(33, 45), ct = b.slice(45);
  const shared = x25519.getSharedSecret(edwardsToMontgomeryPriv(id.secret), ephPub);
  const key = hkdf(sha256, shared, concatBytes(ephPub, id.pub), te('relay/bell/key'), 32);
  const plain = await gcm(key, iv, ct, true);
  const fromPub = plain.slice(0, 32), sig = plain.slice(32, 96), body = plain.slice(96);
  if (!verify(fromPub, sig, 'relay/bell', id.pub, fromPub, body)) throw new Error('bad signature');
  const note = JSON.parse(new TextDecoder().decode(body));
  if (!note || typeof note !== 'object' || note.v !== 1 || !/^[0-9a-f]{32}$/.test(note.tok) || !Number.isSafeInteger(note.at)
    || !Number.isSafeInteger(note.n) || note.n < 1 || note.n > LIMITS.files || !Number.isSafeInteger(note.bytes) || note.bytes < 0) throw new Error('bad note');
  if (Date.now() - note.at > BELL_LIFE || note.at - Date.now() > 10 * 60 * 1000) throw new Error('old note');
  note.name = safeName(String(note.name || 'file'));
  return { fromPub, note };
}
